import { Modal, message } from "antd";
import { useSelector } from "react-redux";
import { useState } from "react";
import "./shareModal.scss";
import facebookIcon from "../../../assets/icons/facebook-logo.svg";
import twitterIcon from "../../../assets/icons/twitter.svg";
import instagramIcon from "../../../assets/icons/instagram.svg";
import starIcon from "../../../assets/icons/star-blue.svg";
import { shareCertificateRequest } from "../../../utils/apis/account";

const ShareModal = ({ isOpen, onClose, certificateUrl, certificateTitle, certificateId }) => {
    const idToken = useSelector((state) => state.auth.idToken);
    const [loadingPlatform, setLoadingPlatform] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    // Get share URL from the backend API
    const getShareUrl = async () => {
        let shareUrlToUse = certificateUrl;
        if (certificateId) {
            try {
                const response = await shareCertificateRequest(idToken, certificateId);
                if (response && response.url) {
                    shareUrlToUse = response.url;
                }
            } catch (error) {
                console.error(error);
            }
        }
        return shareUrlToUse;
    };

    // Try multiple methods to get the certificate image as a Blob
    const getCertificateImageBlob = async () => {
        // Method 1: Direct fetch (works if S3 has CORS headers)
        try {
            const response = await fetch(certificateUrl, { mode: "cors" });
            if (response.ok) {
                const blob = await response.blob();
                if (blob && blob.size > 0) return blob;
            }
        } catch (e) {
            // CORS blocked — try next method
        }

        // Method 2: Load image with crossOrigin and draw to canvas
        try {
            const blob = await new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    try {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.naturalWidth;
                        canvas.height = img.naturalHeight;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob(
                            (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
                            "image/png"
                        );
                    } catch (err) {
                        reject(err);
                    }
                };
                img.onerror = () => reject(new Error("Image load failed"));
                // Add cache-bust to avoid cached non-CORS response
                img.src = certificateUrl + (certificateUrl.includes("?") ? "&" : "?") + "cb=" + Date.now();
            });
            if (blob && blob.size > 0) return blob;
        } catch (e) {
            // Canvas tainted or image failed to load with CORS
        }

        return null;
    };

    // Download the certificate image
    const triggerDownload = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Copy certificate link to clipboard
    const handleCopyLink = async () => {
        try {
            const shareUrl = await getShareUrl();
            await navigator.clipboard.writeText(shareUrl);
            setLinkCopied(true);
            message.success("Certificate link copied to clipboard!");
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (e) {
            message.error("Failed to copy link");
        }
    };

    // Download handler
    const handleDownload = async () => {
        const blob = await getCertificateImageBlob();
        if (blob) {
            triggerDownload(blob, `${certificateTitle || "Certificate"}.png`);
        } else {
            // Fallback: open in new tab for manual save
            window.open(certificateUrl, "_blank");
        }
    };

    const handleShare = async (platform) => {
        setLoadingPlatform(platform);

        const shareUrlToUse = await getShareUrl();
        const shareTextRaw = `Check out my ${certificateTitle} certificate!`;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        // Try to get the image as a blob
        const imageBlob = await getCertificateImageBlob();
        const imageFile = imageBlob
            ? new File([imageBlob], "Certificate.png", { type: "image/png" })
            : null;

        // ═══════════════════════════════════════════
        // MOBILE: Use native share sheet (supports image sharing to all apps)
        // ═══════════════════════════════════════════
        if (isMobile && navigator.share) {
            try {
                const sharePayload = {
                    title: `My ${certificateTitle} Certificate`,
                    text: `${shareTextRaw}\n${shareUrlToUse}`,
                };

                // Attach image if available — this is how images get shared to Instagram/Twitter/etc.
                if (imageFile && navigator.canShare && navigator.canShare({ ...sharePayload, files: [imageFile] })) {
                    sharePayload.files = [imageFile];
                }

                await navigator.share(sharePayload);
                setLoadingPlatform(null);
                onClose();
                return;
            } catch (err) {
                if (err.name === "AbortError") {
                    setLoadingPlatform(null);
                    onClose();
                    return;
                }
                // Fall through to desktop handlers
            }
        }

        // ═══════════════════════════════════════════
        // DESKTOP: Platform-specific handling
        // ═══════════════════════════════════════════
        const shareUrl = encodeURIComponent(shareUrlToUse);
        const shareText = encodeURIComponent(shareTextRaw);

        switch (platform) {
            case "twitter": {
                // If we have the image, auto-download it so user can attach it to the tweet
                if (imageFile) {
                    triggerDownload(imageBlob, `${certificateTitle || "Certificate"}.png`);
                    message.success("Image downloaded! Attach it to your tweet for best results.");
                }
                // Open Twitter compose
                window.open(
                    `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                break;
            }

            case "facebook": {
                // If we have the image, auto-download it
                if (imageFile) {
                    triggerDownload(imageBlob, `${certificateTitle || "Certificate"}.png`);
                    message.success("Image downloaded! Attach it to your Facebook post.");
                }
                // Open Facebook share dialog
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
                    "_blank",
                    "width=600,height=500"
                );
                break;
            }

            case "instagram": {
                // Instagram has no web share intent — download image + copy link
                if (imageFile) {
                    triggerDownload(imageBlob, `${certificateTitle || "Certificate"}.png`);
                }
                try {
                    await navigator.clipboard.writeText(shareUrlToUse);
                    message.success(
                        imageFile
                            ? "Image downloaded & link copied! Share them on Instagram."
                            : "Link copied! Open Instagram app and paste it in a chat or story."
                    );
                } catch (e) {
                    console.error("Clipboard write failed:", e);
                }
                break;
            }

            default:
                break;
        }

        setLoadingPlatform(null);
        onClose();
    };

    const platforms = [
        {
            name: "X (Twitter)",
            key: "twitter",
            icon: twitterIcon,
            color: "#000",
        },
        {
            name: "Instagram",
            key: "instagram",
            icon: instagramIcon,
            gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
        },
        {
            name: "Facebook",
            key: "facebook",
            icon: facebookIcon,
            color: "#1877F2",
        },
        {
            name: "Testimonials",
            key: "testimonials",
            icon: starIcon,
            color: "#36d66b",
            disabled: true,
        },
    ];

    return (
        <Modal
            open={isOpen}
            onCancel={() => {
                setLinkCopied(false);
                setLoadingPlatform(null);
                onClose();
            }}
            footer={null}
            centered
            className="share-certificate-modal"
            width={440}
            style={{
                maxWidth: "calc(100vw - 32px)",
                top: 0
            }}
            styles={{
                body: { padding: 0 }
            }}
        >
            <div className="share-modal-content">
                <h2 className="share-modal-title">Share Certificate</h2>
                <p className="share-modal-subtitle">Share your achievement on social media</p>

                {/* Certificate image preview */}
                <div className="share-certificate-preview">
                    <img src={certificateUrl} alt="Certificate preview" />
                </div>

                {/* Action buttons */}
                <div className="share-action-buttons">
                    <button
                        className={`share-copy-link-btn ${linkCopied ? "copied" : ""}`}
                        onClick={handleCopyLink}
                    >
                        {linkCopied ? "✓ Copied!" : "📋 Copy Link"}
                    </button>
                    <button className="share-download-btn" onClick={handleDownload}>
                        ⬇ Download Image
                    </button>
                </div>

                <p className="share-helper-text">
                    Download the image and upload it when sharing for best results
                </p>

                <div className="share-platforms">
                    {platforms.map((platform) => (
                        <div
                            key={platform.key}
                            className={`share-platform-item ${platform.disabled || loadingPlatform === platform.key ? "disabled" : ""}`}
                            onClick={() =>
                                !platform.disabled && loadingPlatform !== platform.key && handleShare(platform.key)
                            }
                        >
                            <div
                                className="platform-icon"
                                style={{ background: platform.gradient || platform.color }}
                            >
                                <img src={platform.icon} alt={platform.name} />
                            </div>
                            <span className="platform-name">
                                {loadingPlatform === platform.key ? "Sharing..." : platform.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default ShareModal;
